import {
  FontColorEnum,
  FormField,
  FormHint,
  FormLabel,
  RadioButtonToggle,
  Typography,
} from '@acceso/design-system';
import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';

import { TProgram } from '@/features/programs/types';
import { useTranslation } from '@/hooks/use-translation';

interface ProgramSelectorProps<T extends FieldValues> {
  programs: TProgram[];
  form: UseFormReturn<T>;
  name: Path<T>;
  required?: boolean;
  className?: string;
  label?: string;
  disabled?: boolean;
}

export function ProgramSelector<T extends FieldValues>({
  programs,
  form,
  name,
  required,
  className,
  label,
  disabled,
}: ProgramSelectorProps<T>) {
  const { t } = useTranslation();
  const { control } = form;
  const labelSuffix = required ? '*' : '';

  return (
    <FormField className={className}>
      {label && (
        <FormLabel htmlFor={name}>
          {label}
          {labelSuffix}
        </FormLabel>
      )}
      <Controller
        name={name}
        control={control}
        disabled={disabled}
        rules={{
          required: required ? { value: true, message: t('El campo es obligatorio') } : false,
        }}
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const handleClick = (programId: string) => {
            if (disabled) return;
            if (value === programId) {
              onChange(null);
            } else {
              onChange(programId);
            }
          };

          return (
            <>
              {programs.length === 0 ? (
                <Typography>No hay programas disponibles en ese rango temporal</Typography>
              ) : (
                programs.map(program => (
                  <div key={program.id} className="mb-3">
                    <RadioButtonToggle
                      label={program.name}
                      name={name}
                      value={program.id}
                      checked={value === program.id}
                      onClick={handleClick}
                      disabled={disabled}
                    />
                  </div>
                ))
              )}
              {error && (
                <FormHint color={FontColorEnum.Error20} type="error">
                  {error.message}
                </FormHint>
              )}
            </>
          );
        }}
      />
    </FormField>
  );
}
